import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';

/**
 * The form kit (pilot slice 3): one modal and a few fields, reused by every add/edit form so each
 * new module is configuration plus a little code (ADR-0026). No native dialogs (CampusTrack rule).
 */
export function Modal({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: ReactNode; footer: ReactNode; wide?: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-h">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" title={t('drawer.close')} aria-label={t('drawer.close')} onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-b">{children}</div>
        <div className="modal-f">{footer}</div>
      </div>
    </div>
  );
}

interface Base {
  label: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  full?: boolean;
}

export function TextField(p: Base & { value: string; onChange: (v: string) => void; maxLength?: number; lang?: 'ml'; inputMode?: 'tel' | 'numeric' | 'text'; autoComplete?: string; type?: 'text' | 'date' | 'month' | 'search' | 'datetime-local'; max?: string }) {
  const id = useId();
  return (
    <div className={`fld${p.full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {p.label}
        {p.required ? <span className="req" aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        type={p.type ?? 'text'}
        max={p.max}
        value={p.value}
        maxLength={p.maxLength}
        required={p.required}
        disabled={p.disabled}
        lang={p.lang}
        className={p.lang === 'ml' ? 'ml' : undefined}
        inputMode={p.inputMode}
        autoComplete={p.autoComplete ?? 'off'}
        onChange={(e) => p.onChange(e.target.value)}
      />
      {p.hint ? <span className="note">{p.hint}</span> : null}
    </div>
  );
}

export function NumberField(p: Base & { value: number | ''; onChange: (v: number | '') => void; min?: number; max?: number }) {
  const id = useId();
  return (
    <div className={`fld${p.full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {p.label}
        {p.required ? <span className="req" aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        value={p.value}
        min={p.min}
        max={p.max}
        required={p.required}
        disabled={p.disabled}
        onChange={(e) => p.onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
      {p.hint ? <span className="note">{p.hint}</span> : null}
    </div>
  );
}

export function SelectField<V extends string>(p: Base & { value: V | ''; onChange: (v: V | '') => void; options: { value: V; label: string }[]; empty?: string }) {
  const id = useId();
  return (
    <div className={`fld${p.full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {p.label}
        {p.required ? <span className="req" aria-hidden="true"> *</span> : null}
      </label>
      <select id={id} value={p.value} required={p.required} disabled={p.disabled} onChange={(e) => p.onChange(e.target.value as V | '')}>
        {p.empty !== undefined ? <option value="">{p.empty}</option> : null}
        {p.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {p.hint ? <span className="note">{p.hint}</span> : null}
    </div>
  );
}

export function TextAreaField(p: Base & { value: string; onChange: (v: string) => void; maxLength?: number; rows?: number; lang?: 'ml' }) {
  const id = useId();
  return (
    <div className={`fld${p.full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {p.label}
        {p.required ? <span className="req" aria-hidden="true"> *</span> : null}
      </label>
      <textarea
        id={id}
        value={p.value}
        rows={p.rows ?? 4}
        maxLength={p.maxLength}
        required={p.required}
        disabled={p.disabled}
        lang={p.lang}
        className={p.lang === 'ml' ? 'ml' : undefined}
        onChange={(e) => p.onChange(e.target.value)}
      />
      {p.hint ? <span className="note">{p.hint}</span> : null}
    </div>
  );
}

/** A file picker (the register import). The chosen file is handed over whole; nothing is read here. */
export function FileField(p: Base & { accept: string; onChange: (file: File | null) => void }) {
  const id = useId();
  return (
    <div className={`fld${p.full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {p.label}
        {p.required ? <span className="req" aria-hidden="true"> *</span> : null}
      </label>
      <input id={id} type="file" accept={p.accept} required={p.required} disabled={p.disabled} onChange={(e) => p.onChange(e.target.files?.[0] ?? null)} />
      {p.hint ? <span className="note">{p.hint}</span> : null}
    </div>
  );
}

/** Several choices that can all be on at once (e.g. consent purposes). */
export function CheckGroup<V extends string>(p: Base & { value: V[]; onChange: (v: V[]) => void; options: { value: V; label: string; disabled?: boolean }[] }) {
  return (
    <fieldset className={`fld checks${p.full ? ' full' : ''}`} disabled={p.disabled}>
      <legend>
        {p.label}
        {p.required ? <span className="req" aria-hidden="true"> *</span> : null}
      </legend>
      {p.options.map((o) => (
        <label key={o.value} className="check">
          <input
            type="checkbox"
            checked={p.value.includes(o.value)}
            disabled={o.disabled}
            onChange={(e) => p.onChange(e.target.checked ? [...p.value, o.value] : p.value.filter((v) => v !== o.value))}
          />
          {o.label}
        </label>
      ))}
      {p.hint ? <span className="note">{p.hint}</span> : null}
    </fieldset>
  );
}

export function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="fsec">
      <legend>{title}</legend>
      <div className="fgrid">{children}</div>
    </fieldset>
  );
}

export function FormFooter({ onCancel, saving, saveLabel, error }: { onCancel: () => void; saving: boolean; saveLabel: string; error?: string }) {
  const { t } = useTranslation();
  return (
    <>
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <span className="spacer" />
      <button type="button" className="btn" onClick={onCancel}>
        {t('form.cancel')}
      </button>
      <button type="submit" form="active-form" className="btn primary" disabled={saving}>
        {saving ? t('form.saving') : saveLabel}
      </button>
    </>
  );
}

/** Two-step button for actions that take something away, asked inline instead of a browser dialog (CampusTrack rule). */
export function ConfirmButton({ label, onConfirm, disabled, danger }: { label: string; onConfirm: () => void; disabled?: boolean; danger?: boolean }) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button type="button" className={`btn sm${danger ? ' danger' : ''}`} disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </button>
    );
  }
  return (
    <span className="confirm" role="group" aria-label={label}>
      <span>{t('users.confirm')}</span>
      <button type="button" className={`btn sm${danger ? ' danger' : ' primary'}`} onClick={() => { setAsking(false); onConfirm(); }}>
        {t('users.yes')}
      </button>
      <button type="button" className="btn sm" onClick={() => setAsking(false)}>
        {t('users.no')}
      </button>
    </span>
  );
}
